import { TFunction } from "i18next";

export interface IotStatusConfig {
    status: string;
    color: string;
    label: string;
    /**
     * Wording used when the status belongs to a system instead of an asset.
     * Only needed where the default label names the entity ("Asset is turned
     * off"); statuses with neutral wording fall back to `label`.
     */
    systemLabel?: string;
    /**
     * Wording used in CSV exports, where the UI sentence ("Asset is turned
     * off") reads badly in a spreadsheet cell. Statuses whose label is already
     * a short term fall back to `label`.
     */
    exportLabel?: string;
    /** Text color used when this status drives the donut marker center fill */
    centerTextColor: string;
    /** Background of the status chip shown in lists and on nameplates */
    chipBg: string;
    /** Text color of the status chip */
    chipText: string;
}

/**
 * IoT connection status configuration.
 * Order defines:
 *   1. Priority for the donut marker center color (first match wins)
 *   2. Segment order in the donut ring
 *   3. Legend order in the IoT Assets pie chart widget
 */
export const IOT_STATUS_CONFIG: IotStatusConfig[] = [
    { status: "ERROR",             color: "#CC0000", label: "Alarm",               centerTextColor: "white", chipBg: "#EE8E8E", chipText: "#333333" },
    { status: "WARNING",           color: "#FFC860", label: "Warning",             centerTextColor: "#333",  chipBg: "#FFDC99", chipText: "#333333" },
    { status: "RUNNING",           color: "#599460", label: "Running",             centerTextColor: "white", chipBg: "#BDE3CA", chipText: "#304933" },
    { status: "OFF",               color: "#7F7F7F", label: "Asset is turned off", systemLabel: "System is turned off", exportLabel: "OFF", centerTextColor: "white", chipBg: "#F4F4F4", chipText: "#333333" },
    { status: "DATA_BUFFERING",    color: "#19B6BE", label: "Data Buffering",      centerTextColor: "white", chipBg: "#8BDADD", chipText: "#333333" },
    { status: "CURRENTLY_OFFLINE", color: "#90ACC8", label: "Currently Offline",   centerTextColor: "#333",  chipBg: "#ABC0D5", chipText: "#333333" },
    { status: "NEVER_CONNECTED",   color: "#2E2E2E", label: "Never Connected",     centerTextColor: "white", chipBg: "#F4F4F4", chipText: "#333333" },
];

export const IOT_STATUS_COLOR_MAP: Record<string, string> = Object.fromEntries(
    IOT_STATUS_CONFIG.map((c) => [c.status, c.color])
);

/**
 * Translation-key map for every IoT connection status enum. Values are i18n
 * keys — render sites use `getIotStatusLabel` instead of reading this directly,
 * so an unknown status can never leak into the UI as a raw enum string.
 */
const IOT_STATUS_LABEL: Record<string, string> = Object.fromEntries(
    IOT_STATUS_CONFIG.map((c) => [c.status, c.label])
);

/** Same map for systems; statuses without their own wording reuse `label`. */
const IOT_STATUS_SYSTEM_LABEL: Record<string, string> = Object.fromEntries(
    IOT_STATUS_CONFIG.map((c) => [c.status, c.systemLabel ?? c.label])
);

/** Same map for CSV exports; statuses without their own wording reuse `label`. */
const IOT_STATUS_EXPORT_LABEL: Record<string, string> = Object.fromEntries(
    IOT_STATUS_CONFIG.map((c) => [c.status, c.exportLabel ?? c.label])
);

export interface IotStatusLabelOptions {
    /** Render the system wording (e.g. "System is turned off"). */
    isSystem?: boolean;
    /** Render the short CSV-export wording instead of the UI sentence. */
    forExport?: boolean;
}

export interface IotStatusChipColors {
    bg: string;
    text: string;
}

const FALLBACK_CHIP_COLORS: IotStatusChipColors = { bg: "#F4F4F4", text: "#333333" };

const IOT_STATUS_CHIP_COLOR_MAP: Record<string, IotStatusChipColors> = Object.fromEntries(
    IOT_STATUS_CONFIG.map((c) => [c.status, { bg: c.chipBg, text: c.chipText }])
);

/**
 * Localized label for an IoT connection status. Pass `{ isSystem: true }` when
 * the status belongs to a system so entity-specific wording reads correctly, or
 * `{ forExport: true }` for the short wording used in CSV cells.
 * Unknown values (e.g. an enum value added by the backend before the frontend
 * knows it) are humanized instead of surfacing the raw `SCREAMING_SNAKE` string.
 */
export const getIotStatusLabel = (
    status: string | null | undefined,
    t: TFunction | ((key: string) => string),
    { isSystem, forExport }: IotStatusLabelOptions = {}
): string => {
    if (!status) {
        return "";
    }

    const labelKey = forExport
        ? IOT_STATUS_EXPORT_LABEL[status]
        : isSystem
          ? IOT_STATUS_SYSTEM_LABEL[status]
          : IOT_STATUS_LABEL[status];
    if (labelKey) {
        return t(labelKey) as string;
    }

    const humanized = status.replace(/_/g, " ").toLowerCase();
    return humanized.charAt(0).toUpperCase() + humanized.slice(1);
};

/** Chip background/text colors for an IoT connection status. */
export const getIotStatusChipColors = (status: string | null | undefined): IotStatusChipColors =>
    (status && IOT_STATUS_CHIP_COLOR_MAP[status]) || FALLBACK_CHIP_COLORS;
